import { useEffect, useRef } from 'react';

import { useUpsertMyProfileMutation } from '@/services/socialApi';
import { useTelegram } from '@/hooks/useTelegram';
import { useAppSelector } from '@/store/hooks';
import { laravelOwnsData } from '@/lib/dataOwner';
import { dbg } from '@/lib/logto';
import { env } from '@/env';
import type { UpsertMyProfileInput } from '@/types/social';

/**
 * Syncs the authenticated user's QSocials profile (displayName + avatar) once
 * per mount/session.
 *
 * The public display name is the user's **QLASH account handle** — their
 * `username` from `/users/me`, falling back to the Telegram `@username`. We
 * deliberately NEVER publish the Telegram first/last name: a player is referred
 * to by their QLASH account, never by their legal name (personal data). QSocials
 * search + friend lists key off this displayName, so publishing the handle is
 * exactly what makes people findable by their QLASH account and not their name.
 *
 * QSocials stores friend/follow rows keyed by Logto `sub`, plus a `userProfiles`
 * collection (displayName + avatar) so lists show a handle instead of a raw sub.
 * The Mini App pushes it via `PUT /social/me/profile` on launch. Idempotent
 * server-side — re-running with the same payload is a no-op write.
 *
 * Mount once at the AppShell layer (after auth), alongside usePresenceHeartbeat.
 * No-op until `bridgeAuthenticated` is true and a usable handle is present
 * (plain-web/dev runs without a Telegram user still sync off the QLASH username).
 */
export function useProfileSync(): void {
  const isAuthenticated = useAppSelector((s) => s.auth.bridgeAuthenticated);
  // ═══ LA GUARDIA SUL NOME PUBBLICO — e sta sulla CONDIZIONE, non sul valore ═══
  //
  // 🔴 Misurato su `stg-tma` il 2026-09-10: questo hook ha pubblicato **l'`@username`
  // Telegram** come nome pubblico di una persona. Non era `"Player"`, e ha scritto lo
  // stesso. ⇒ ***Una guardia scritta come «non pubblicare se e' "Player"» non avrebbe
  // coperto niente***: il valore che arriva quando manca un nome scelto **non e' uno solo**.
  //
  // ⚠️ E il momento conta: la riga e' stata scritta **prima che `/v1/me` rispondesse**, cioe'
  // con `qlashUsername` ancora vuoto ⇒ **e' scattato il ripiego Telegram della riga sotto**.
  // ⇒ *Il caso pericoloso non e' solo «so che manca il nickname»: e' anche **«non lo so
  // ancora»**, e i due si assomigliano perche' entrambi hanno un valore da pubblicare.*
  //
  // ⇒ **La regola: sotto Limulo si pubblica SOLO con un `requiresNickname === false`
  // esplicito.** `true` (manca la scelta) e `undefined` (identita' non ancora arrivata, o
  // sorgente che non espone il campo) **si comportano uguale: non si pubblica.**
  // ⛔ `undefined` NON e' `false`: collassarli rimetterebbe esattamente il difetto misurato.
  //
  // ⚠️ **Sotto il monolite non cambia NIENTE**: `featureLimuloEnabled` e' spento in
  // produzione, il campo non esiste in quella busta, e questo ramo non si applica — la
  // guardia non deve togliere a chi oggi funziona (ADR-0125 riguarda la facciata Limulo).
  //
  // 📌 Ciò che va al posto del nome — l'invito a scegliere il nickname — **non e' qui**: e'
  // una superficie, ed e' il §6-bis di `reports/2026-09-10-profilo-nickname-caso-null-disegno.md`.
  const limuloOwnsIdentity = env.featureLimuloEnabled;
  const requiresNickname = useAppSelector((s) => s.auth.user?.requiresNickname);
  // ⛔ I TRE STATI SI SCRIVONO, e non si collassano in un booleano: **il terzo e' l'unico
  // che il difetto usa.** Un `if (requiresNickname)` sarebbe cieco proprio li' — `undefined`
  // e' falsy, quindi non ritornerebbe, e si pubblicherebbe lo stesso.
  const nickname: 'scelto' | 'da-scegliere' | 'non-lo-so-ancora' =
    requiresNickname === false
      ? 'scelto'
      : requiresNickname === true
        ? 'da-scegliere'
        : 'non-lo-so-ancora';
  // QLASH user from `/users/me`: `username` is the public account handle we
  // publish; `id` is the Laravel numeric users.id we self-publish as
  // `externalUserId` so the participants list can resolve us deterministically
  // (immune to Apple Hide-My-Email / social-login email mismatches).
  const qlashUsername = useAppSelector((s) => s.auth.user?.username);
  const qlashUserId = useAppSelector((s) => s.auth.user?.id);
  const { user } = useTelegram();
  const [upsertProfile] = useUpsertMyProfileMutation();

  // Guard so we sync at most once per mount even if deps re-run (the QLASH user
  // is stable once loaded, but RTK's mutation trigger identity is not).
  const syncedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || syncedRef.current) return;
    // ⛔ Non marca `syncedRef`: quando `requiresNickname` diventa `false` — la persona
    // sceglie il nickname, o `/v1/me` risponde — l'effetto rigira e pubblica. Marcarlo qui
    // renderebbe la guardia definitiva per tutta la sessione.
    // Predicato POSITIVO: si pubblica solo su un «scelto» affermato. Gli altri due —
    // compreso «non lo so ancora» — non pubblicano.
    // ⚠️ PERIMETRO DELLA GUARDIA, dichiarato perché la sua assenza non si vede:
    // questo ramo vale SOLO con `featureLimuloEnabled` acceso. Con esso spento — cioè in
    // produzione, oggi — si pubblica `handleLaravel` senza passare di qui, ed è voluto.
    // ⇒ Chi accende l'identità Limulo in prod eredita `VITE_FEATURE_SOCIAL_ENABLED`, che è
    //   DEFAULT-ON e senza riga in `.env.production`: vedi la nota su `featureLimuloEnabled`
    //   in `env.ts`. La guardia protegge il COSA si pubblica, non il SE si pubblica.
    if (limuloOwnsIdentity && nickname !== 'scelto') return;

    // QLASH account handle ONLY — never the Telegram legal name. Prefer the
    // QLASH username; fall back to the Telegram @username (also a public handle).
    const displayName = qlashUsername?.trim() || user?.username?.trim() || null;
    if (!displayName) return; // no handle to publish yet

    const payload: UpsertMyProfileInput = { displayName };
    if (user?.photo_url) payload.avatarUrl = user.photo_url;
    // ═══ `externalUserId` E' UN ID LARAVEL — E IL PREDICATO STA SULLA PROVENIENZA ═══
    //
    // `auth.user.id` **cambia SPECIE con la build**: id numerico Laravel da `GET /users/me`,
    // ObjectId Mongo di Limulo da `GET /v1/me` (`lib/limulo/identity.ts:70`). Questa riga
    // pubblicava il primo che capitava.
    //
    // 🔴 E il danno non e' «un id della specie sbagliata memorizzato»: QSocials **RIFIUTA
    // TUTTA LA BUSTA**. `upsertProfile` valida `externalUserId` con `/^[0-9]+$/` e un massimo
    // di 20 caratteri e **lancia `400 invalid_external_user_id` PRIMA** di comporre il `$set`
    // (`qsocials-api/src/modules/userProfiles/service.ts:6,12,44-62`; un ObjectId di 24 hex
    // sbaglia entrambi i vincoli) ⇒ sotto identita' Limulo `PUT /social/me/profile` non
    // scriveva NIENTE: **nemmeno `displayName` e `avatarUrl`**. ⚠️ Un profilo mai scritto
    // somiglia a un profilo mai aperto: nessuno schermo lo distingue.
    // [M or57 sul suo repo, verificata in sola lettura da qui sul default branch di
    //  `Qlash-IT/qsocials` — non provata dal vivo: da qui non chiamiamo QSocials.]
    //
    // ⛔ IL PREDICATO E' SULLA PROVENIENZA, MAI SULLA FORMA DELLA STRINGA. «non sembra un
    // ObjectId» e' una forma INDOVINATA: il giorno che una terza sorgente manda un id
    // numerico che non e' di Laravel, quella guardia dice si' e nessun test cambia colore.
    // `laravelOwnsData()` (`lib/dataOwner.ts`) e' la sede UNICA della domanda «di chi e' lo
    // spazio di id di questa build», e il suo blocco in testa nomina esattamente questo
    // guasto. Si riusa quella, e non il `!limuloOwnsIdentity` di tre righe sopra: quel
    // booleano e' la lettura locale per la guardia sul NOME, e due copie della stessa
    // domanda divergono alla prima revisione.
    // ⇒ Sotto Limulo il campo **non si pubblica**: ometterlo lascia intatto il valore gia'
    //   memorizzato (contratto di `upsertProfile`, riga 40-42), e il resto del profilo passa.
    if (laravelOwnsData() && qlashUserId != null) payload.externalUserId = String(qlashUserId);

    syncedRef.current = true;
    // Fire-and-forget — a failed profile sync must never block the UI.
    upsertProfile(payload)
      .unwrap()
      .catch((err: unknown) => {
        // Allow a retry on the next auth/user change if the write failed.
        syncedRef.current = false;
        // 🔴 UN 4xx PARLA ANCHE FUORI DA DEV. Prima di oggi l'unica riga stava sotto
        // `import.meta.env.DEV` ⇒ in staging e in produzione il rifiuto di QSocials era
        // **muto**, e un profilo mai scritto si presenta come un profilo mai aperto: e'
        // cosi' che il 400 su `externalUserId` e' passato inosservato.
        // ⚠️ La distinzione non e' cosmetica: **un 4xx e' il contratto che dice no** — si
        // ripresenta identico a ogni giro e nessuno lo aggiusta da solo — mentre una rete
        // che cade e' rumore che passa. Registrare tutto avrebbe lo stesso effetto del
        // silenzio: chi legge smette di guardare.
        // Sede: `dbg()` di `lib/logto.ts`, la stessa dei vicini (scrive in console E nel
        // registro `localStorage`, che e' l'unico che sopravvive alla WebView, e redige
        // alla fonte). Nessuna sede nuova.
        const status = (err as { status?: unknown } | null | undefined)?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          dbg(`[profile-sync] rifiuto ${status}: il profilo NON e' stato scritto`);
        }
        if (import.meta.env.DEV) {
          console.debug('[profile-sync] upsert failed:', err);
        }
      });
  }, [
    isAuthenticated,
    limuloOwnsIdentity,
    nickname,
    qlashUsername,
    qlashUserId,
    user,
    upsertProfile,
  ]);
}
